/**
 * A received message's attachments as every mail client draws them (piece 32;
 * T347; decision 0994): a count with Download all beside it, and one card per
 * attachment carrying a type glyph, the file's name and its size. The pure
 * rules live here, so the reading pane and the zip route read one answer.
 */
import type { IconMeaning } from '../icons';
import { mailMessagePath } from './client';
import { t } from '../strings';
import { byteText } from '../catalog/itemImage';

/** The six kinds a card's glyph tells apart. */
export const MAIL_ATTACHMENT_KINDS = ['pdf', 'image', 'spreadsheet', 'document', 'archive', 'other'] as const;
export type MailAttachmentKind = (typeof MAIL_ATTACHMENT_KINDS)[number];

/** Each kind's glyph, as the shared icon reference names it. */
export const MAIL_ATTACHMENT_KIND_ICON: Readonly<Record<MailAttachmentKind, IconMeaning>> = {
  pdf: 'attachmentPdf',
  image: 'attachmentImage',
  spreadsheet: 'attachmentSpreadsheet',
  document: 'attachmentDocument',
  archive: 'attachmentArchive',
  other: 'attachmentOther',
};

const BY_EXTENSION: Readonly<Record<string, MailAttachmentKind>> = {
  pdf: 'pdf',
  png: 'image', jpg: 'image', jpeg: 'image', gif: 'image', webp: 'image', bmp: 'image', tif: 'image', tiff: 'image', heic: 'image', svg: 'image',
  xls: 'spreadsheet', xlsx: 'spreadsheet', xlsm: 'spreadsheet', csv: 'spreadsheet', ods: 'spreadsheet', numbers: 'spreadsheet',
  doc: 'document', docx: 'document', odt: 'document', rtf: 'document', txt: 'document', pages: 'document',
  zip: 'archive', '7z': 'archive', rar: 'archive', gz: 'archive', tgz: 'archive', tar: 'archive',
};

/**
 * The kind an attachment is, read from its media type and then its name's
 * extension, because a sender's mail program often labels a file only by one
 * of the two. Anything neither names is `other`.
 */
export function mailAttachmentKind(contentType: string, filename: string): MailAttachmentKind {
  const type = contentType.toLowerCase().split(';')[0]!.trim();
  if (type === 'application/pdf') return 'pdf';
  if (type.startsWith('image/')) return 'image';
  if (/spreadsheet|excel|text\/csv|opendocument\.spreadsheet/.test(type)) return 'spreadsheet';
  if (/msword|wordprocessing|opendocument\.text|rtf|^text\/plain$/.test(type)) return 'document';
  if (/zip|x-7z|x-rar|gzip|x-tar|compressed/.test(type)) return 'archive';
  const dot = filename.lastIndexOf('.');
  return (dot >= 0 ? BY_EXTENSION[filename.slice(dot + 1).toLowerCase()] : undefined) ?? 'other';
}

/** A size as a person reads it, in the mail pane's translated units: the shared file-size text. */
export function mailAttachmentSize(bytes: number, units: { kb: string; mb: string }): string {
  return byteText(bytes, units);
}

/**
 * 🔒 Download all's bound (piece 32; T357; decision 0994): the zip is built whole
 * in the API's memory, so the attachments it carries total 50 MB at most. A
 * message past it offers each card alone, which streams one file at a time.
 */
export const MAIL_ATTACHMENTS_ZIP_LIMIT_BYTES = 50 * 1024 * 1024;

/** The attachments' total where it is past Download all's bound, and null where the zip is built. */
export function attachmentsZipOverLimit(sizes: readonly number[]): number | null {
  const total = sizes.reduce((sum, size) => sum + size, 0);
  return total > MAIL_ATTACHMENTS_ZIP_LIMIT_BYTES ? total : null;
}

/**
 * Why Download all is refused past its bound, naming the total and the way that
 * still works: each attachment from its own card. One sentence for the pane's
 * greyed control and the route's refusal.
 */
export function attachmentsZipTooLargeReason(totalBytes: number): string {
  const units = { kb: t('mail.unit.kb'), mb: t('mail.unit.mb') };
  const limit = `${MAIL_ATTACHMENTS_ZIP_LIMIT_BYTES / (1024 * 1024)} ${units.mb}`;
  return `${t('mail.pane.downloadAllTooLarge.lead')} ${mailAttachmentSize(totalBytes, units)}, ${t('mail.pane.downloadAllTooLarge.over')} ${limit} ${t('mail.pane.downloadAllTooLarge.tail')}`;
}

/** Text with its control characters gone, which no file name carries. */
const withoutControls = (text: string): string => [...text].filter((c) => c.charCodeAt(0) >= 0x20 && c.charCodeAt(0) !== 0x7f).join('');

/**
 * The names the entries of Download all's zip take: a path, a control
 * character or an empty name never passes into it, and a name that repeats
 * gains " (2)", " (3)" before its extension, so no entry overwrites another
 * when the zip is opened.
 */
export function attachmentZipNames(filenames: readonly string[]): string[] {
  const taken = new Set<string>();
  return filenames.map((raw) => {
    const base = withoutControls(raw.replace(/^.*[\\/]/, '')).trim() || 'attachment';
    const dot = base.lastIndexOf('.');
    const stem = dot > 0 ? base.slice(0, dot) : base;
    const extension = dot > 0 ? base.slice(dot) : '';
    let name = base;
    for (let n = 2; taken.has(name.toLowerCase()); n++) name = `${stem} (${n})${extension}`;
    taken.add(name.toLowerCase());
    return name;
  });
}

/** The zip's own file name: the message's subject made safe for a file, or `attachments` where it names nothing. */
export function mailAttachmentsZipName(subject: string): string {
  const safe = withoutControls(subject.replace(/[\\/:*?"<>|]/g, ' ')).replace(/\s+/g, ' ').trim().slice(0, 80).trim();
  return `${safe || 'attachments'}.zip`;
}

/** Download all: every attachment of one message as one zip, relative to the API root. */
export const mailAttachmentsZipPath = (messageId: string): string => `${mailMessagePath(messageId)}/attachments/all`;
